import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetView,
  type BottomSheetBackdropProps
} from '@gorhom/bottom-sheet'
import { useFocusEffect } from '@react-navigation/native'
import { forwardRef, useCallback, useImperativeHandle, useRef, type FC } from 'react'
import { View } from 'react-native'
import { graphql, useFragment } from 'react-relay'

import { type ProfileMatchBottomSheet_fromProfile$key } from '@/__generated__/ProfileMatchBottomSheet_fromProfile.graphql'
import { type ProfileMatchBottomSheet_toProfile$key } from '@/__generated__/ProfileMatchBottomSheet_toProfile.graphql'
import { BigRedButton, Center, HStack, ProfileAvatar, Text, VStack } from '@/components'
import { useAppReview, useBottomSheetContainerStyle, useFocusableRef, usePreloadableRouter } from '@/hooks'
import {
  redBottomSheetModalBackgroundStyle,
  redBottomSheetModalHandleIndicatorStyle,
  redBottomSheetModalHandleStyle
} from '@/lib'

export type ProfileMatchBottomSheetData = {
  matchId: string
}
export type ProfileMatchBottomSheetRef = BottomSheetModal<ProfileMatchBottomSheetData>

export interface LikeProfileViewProps {
  fromProfileRef: ProfileMatchBottomSheet_fromProfile$key | null | undefined
  toProfileRef: ProfileMatchBottomSheet_toProfile$key | null | undefined
}

export const ProfileMatchBottomSheet = forwardRef<ProfileMatchBottomSheetRef, LikeProfileViewProps>(
  ({ fromProfileRef, toProfileRef }, ref) => {
    const [headerRef, focusHeader] = useFocusableRef<View>(true)
    const router = usePreloadableRouter()
    const { requestReviewPrompt } = useAppReview()
    const navigatingRef = useRef(false)
    const bottomSheetModalRef = useRef<BottomSheetModal>(null)
    const fromProfileData = useFragment(
      graphql`
        fragment ProfileMatchBottomSheet_fromProfile on Profile {
          name
          rowId
          ...ProfileAvatar_profile
        }
      `,
      fromProfileRef
    )
    const toProfileData = useFragment(
      graphql`
        fragment ProfileMatchBottomSheet_toProfile on Profile {
          name
          rowId
          ...ProfileAvatar_profile
        }
      `,
      toProfileRef
    )

    useImperativeHandle(ref, () => bottomSheetModalRef.current!)

    return (
      <BottomSheetModal<ProfileMatchBottomSheetData>
        accessible={false}
        accessibilityRole='alert'
        accessibilityLiveRegion='assertive'
        backdropComponent={useCallback(
          (props: BottomSheetBackdropProps) => (
            <BottomSheetBackdrop
              {...props}
              accessible={false}
              accessibilityElementsHidden
              appearsOnIndex={0}
              aria-hidden
              disappearsOnIndex={-1}
              importantForAccessibility='no-hide-descendants'
            />
          ),
          []
        )}
        backgroundStyle={redBottomSheetModalBackgroundStyle}
        containerStyle={useBottomSheetContainerStyle()}
        enableDismissOnClose
        enableDynamicSizing
        enablePanDownToClose
        handleIndicatorStyle={redBottomSheetModalHandleIndicatorStyle}
        handleStyle={redBottomSheetModalHandleStyle}
        ref={bottomSheetModalRef}
        role='alertdialog'
        onChange={useCallback(
          (position: number) => {
            // opened
            if (position === 0) {
              focusHeader()
            } else if (position === -1) {
              // closed
              if (navigatingRef.current) {
                navigatingRef.current = false
              } else {
                requestReviewPrompt('match')
              }
            }
          },
          [focusHeader, requestReviewPrompt]
        )}
      >
        {useCallback<FC<{ data?: ProfileMatchBottomSheetData | undefined }>>(
          ({ data }) => (
            <BottomSheetView focusHook={useFocusEffect}>
              <VStack className='pb-safe-or-4 flex-1 items-center gap-4 p-4'>
                <HStack className='items-center justify-center'>
                  <Center className='-mr-10 rounded-full border border-dashed border-white p-1'>
                    <ProfileAvatar
                      className='z-10 h-32 w-32 border-2 border-brand-orange-500'
                      profileRef={toProfileData}
                    />
                  </Center>
                  <Center className='rounded-full border border-dashed border-white p-1'>
                    <ProfileAvatar
                      className='h-32 w-32 border-2 border-white'
                      profileRef={fromProfileData}
                    />
                  </Center>
                </HStack>
                <HStack
                  accessible
                  accessibilityLabel="It's a match!"
                  className='gap-1'
                  focusable
                  importantForAccessibility='yes'
                  ref={headerRef}
                  tabIndex={0}
                >
                  <Text
                    accessible={false}
                    accessibilityElementsHidden
                    aria-hidden
                    importantForAccessibility='no-hide-descendants'
                    className='text-center font-poppinsSemiBold text-white'
                    size='lg'
                  >
                    It's a match!
                  </Text>
                </HStack>
                <Text className='text-center text-white'>
                  You can chat with {toProfileData?.name} in your Messages tab.
                </Text>
                <BigRedButton
                  accessibilityLabel={`Chat with ${toProfileData?.name} now`}
                  onPress={() => {
                    // Set before dismiss() so the onChange(-1) close handler skips the review
                    // prompt on the navigate-to-chat path (the message trigger covers it instead).
                    navigatingRef.current = true
                    bottomSheetModalRef.current?.dismiss()
                    if (data?.matchId) {
                      router.replace(`/messages/${data.matchId}`, {
                        queryName: 'MatchProfileScreenQuery',
                        queryVariables: {
                          matchId: data.matchId
                        }
                      })
                    }
                  }}
                  testID='ProfileMatchBottomSheet.messageButton'
                  variant='outline'
                >
                  {`Message ${toProfileData?.name}`}
                </BigRedButton>
              </VStack>
            </BottomSheetView>
          ),
          [fromProfileData, headerRef, router, toProfileData]
        )}
      </BottomSheetModal>
    )
  }
)

ProfileMatchBottomSheet.displayName = 'ProfileMatchBottomSheet'
